import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Paper,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  format,
  addMonths,
  subMonths,
  startOfDay,
  getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';

type UrlaubStatus = 'Beantragt' | 'Genehmigt' | 'Abgelehnt';

/** Generisches Kalender-Item – unabhaengig von der Datenquelle (eigener Urlaub oder alle Mitarbeiter). */
export interface UrlaubKalenderItem {
  id: number | string;
  label: string;
  von: string;
  bis: string;
  status: string;
  anzahl_tage: number;
  bemerkung?: string;
}

interface UrlaubEvent {
  id: string;
  label: string;
  von: Date;
  bis: Date;
  status: UrlaubStatus;
  tage: number;
  bemerkung?: string;
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const STATUS_COLORS: Record<UrlaubStatus, string> = {
  Genehmigt: '#4caf50',
  Beantragt: '#ff9800',
  Abgelehnt: '#ef5350',
};

const getStatusColor = (status: string): string =>
  STATUS_COLORS[status as UrlaubStatus] ?? '#8899aa';

const parseLocalDate = (dateStr: string): Date => new Date(`${dateStr}T00:00:00`);

interface EventPlacement {
  event: UrlaubEvent;
  startCol: number;
  endCol: number;
  lane: number;
  isStart: boolean;
  isEnd: boolean;
}

const getGlobalLaneAssignments = (allEvents: UrlaubEvent[]): Map<string, number> => {
  const sorted = [...allEvents].sort((a, b) => {
    const aDuration = startOfDay(a.bis).getTime() - startOfDay(a.von).getTime();
    const bDuration = startOfDay(b.bis).getTime() - startOfDay(b.von).getTime();
    if (bDuration !== aDuration) return bDuration - aDuration;
    return startOfDay(a.von).getTime() - startOfDay(b.von).getTime();
  });

  const assignments = new Map<string, number>();
  const laneOccupancy: { start: number; end: number }[][] = [];

  for (const event of sorted) {
    const evStart = startOfDay(event.von).getTime();
    const evEnd = startOfDay(event.bis).getTime();

    let lane = 0;
    while (lane < 100) {
      if (!laneOccupancy[lane]) laneOccupancy[lane] = [];
      const hasConflict = laneOccupancy[lane].some(
        (range) => evStart <= range.end && evEnd >= range.start
      );
      if (!hasConflict) break;
      lane++;
    }

    if (!laneOccupancy[lane]) laneOccupancy[lane] = [];
    laneOccupancy[lane].push({ start: evStart, end: evEnd });
    assignments.set(event.id, lane);
  }

  return assignments;
};

const getWeekEventPlacements = (
  week: Date[],
  allEvents: UrlaubEvent[],
  laneMap: Map<string, number>
): EventPlacement[] => {
  const weekStartTime = startOfDay(week[0]).getTime();
  const weekEndTime = startOfDay(week[6]).getTime();

  const overlapping = allEvents.filter((ev) => {
    const evStartTime = startOfDay(ev.von).getTime();
    const evEndTime = startOfDay(ev.bis).getTime();
    return evStartTime <= weekEndTime && evEndTime >= weekStartTime;
  });

  const placements: EventPlacement[] = [];

  for (const event of overlapping) {
    const evStartTime = startOfDay(event.von).getTime();
    const evEndTime = startOfDay(event.bis).getTime();

    let startCol = 0;
    if (evStartTime > weekStartTime) {
      const idx = week.findIndex((d) => startOfDay(d).getTime() === evStartTime);
      startCol = idx >= 0 ? idx : 0;
    }

    let endCol = 6;
    if (evEndTime < weekEndTime) {
      const idx = week.findIndex((d) => startOfDay(d).getTime() === evEndTime);
      endCol = idx >= 0 ? idx : 6;
    }

    const isStart = evStartTime >= weekStartTime;
    const isEnd = evEndTime <= weekEndTime;
    const lane = laneMap.get(event.id) ?? 0;

    placements.push({ event, startCol, endCol, lane, isStart, isEnd });
  }

  return placements;
};

const renderEventTooltip = (ev: UrlaubEvent) => (
  <Box>
    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{ev.label}</Typography>
    <Typography sx={{ fontSize: '0.7rem', mt: 0.3, color: '#ccc' }}>
      {`${format(ev.von, 'dd.MM.yyyy')} – ${format(ev.bis, 'dd.MM.yyyy')}`}
    </Typography>
    <Typography sx={{ fontSize: '0.7rem', mt: 0.3, color: '#ccc' }}>
      {ev.tage} Tag{ev.tage === 1 ? '' : 'e'} · {ev.status}
    </Typography>
    {ev.bemerkung && (
      <Typography sx={{ fontSize: '0.7rem', mt: 0.5, color: '#aab' }}>{ev.bemerkung}</Typography>
    )}
  </Box>
);

const LEGEND: { label: string; status: UrlaubStatus }[] = [
  { label: 'Genehmigt', status: 'Genehmigt' },
  { label: 'Beantragt', status: 'Beantragt' },
  { label: 'Abgelehnt', status: 'Abgelehnt' },
];

interface UrlaubKalenderProps {
  items: UrlaubKalenderItem[];
  selectedYear: number;
  isDark: boolean;
  emptyHint?: string;
}

const UrlaubKalender: React.FC<UrlaubKalenderProps> = ({ items, selectedYear, isDark, emptyHint }) => {
  const theme = useTheme();

  const cal = {
    paperBg: isDark ? '#212744' : '#ffffff',
    border: isDark ? '#2e3560' : '#dfe3e8',
    headerBg: isDark ? '#2c3358' : '#f5f7fa',
    textPrimary: isDark ? '#e8edf3' : '#1a2332',
    textSecondary: isDark ? '#8899aa' : '#5f7082',
    textMuted: isDark ? '#556677' : '#8899aa',
    accent: theme.palette.primary.main,
    todayBg: isDark ? '#2c335880' : 'rgba(43,168,98,0.08)',
    outOfMonthBg: isDark ? '#181c3410' : 'rgba(0,0,0,0.02)',
    hoverBg: isDark ? '#2c335840' : 'rgba(0,0,0,0.04)',
    todayHighlight: isDark ? '#3dd47e20' : 'rgba(43,168,98,0.12)',
  };

  const initialDate = useMemo(() => {
    const now = new Date();
    return now.getFullYear() === selectedYear ? now : new Date(selectedYear, 0, 1);
  }, [selectedYear]);

  const [currentDate, setCurrentDate] = useState(initialDate);
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());

  useEffect(() => {
    const now = new Date();
    setCurrentDate(now.getFullYear() === selectedYear ? now : new Date(selectedYear, 0, 1));
  }, [selectedYear]);

  const toggleStatus = (status: string) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const events = useMemo<UrlaubEvent[]>(
    () =>
      items.map((it) => ({
        id: `u-${it.id}`,
        label: it.label,
        von: parseLocalDate(it.von),
        bis: parseLocalDate(it.bis),
        status: (it.status as UrlaubStatus) ?? 'Beantragt',
        tage: it.anzahl_tage,
        bemerkung: it.bemerkung,
      })),
    [items]
  );

  const filteredEvents = useMemo(
    () => events.filter((ev) => !hiddenStatuses.has(ev.status)),
    [events, hiddenStatuses]
  );

  const globalLaneMap = useMemo(() => getGlobalLaneAssignments(filteredEvents), [filteredEvents]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentDate]);

  const weeks: Date[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const handlePrevMonth = () => setCurrentDate((d) => subMonths(d, 1));
  const handleNextMonth = () => setCurrentDate((d) => addMonths(d, 1));
  const handleToday = () => setCurrentDate(new Date());

  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = { Genehmigt: 0, Beantragt: 0, Abgelehnt: 0 };
    for (const ev of events) counts[ev.status] = (counts[ev.status] ?? 0) + 1;
    return counts;
  }, [events]);

  return (
    <Box>
      {/* Navigation Header */}
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: 3,
          py: 1.5,
          mb: 2,
          borderRadius: 3,
          bgcolor: cal.paperBg,
          border: `1px solid ${cal.border}`,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<TodayIcon />}
          onClick={handleToday}
          size="small"
          sx={{
            position: 'absolute',
            left: 16,
            borderColor: cal.border,
            color: cal.textSecondary,
            '&:hover': { borderColor: cal.accent, color: cal.accent },
          }}
        >
          Heute
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={handlePrevMonth}
            sx={{ color: cal.textSecondary, '&:hover': { color: cal.accent } }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: cal.textPrimary,
              minWidth: 220,
              textAlign: 'center',
            }}
          >
            {format(currentDate, 'MMMM yyyy', { locale: de })}
          </Typography>
          <IconButton
            onClick={handleNextMonth}
            sx={{ color: cal.textSecondary, '&:hover': { color: cal.accent } }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Legende (klickbar als Filter) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {LEGEND.map((item) => {
          const isHidden = hiddenStatuses.has(item.status);
          return (
            <Box
              key={item.status}
              onClick={() => toggleStatus(item.status)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                opacity: isHidden ? 0.35 : 1,
                transition: 'opacity 0.2s',
                userSelect: 'none',
                '&:hover': { opacity: isHidden ? 0.5 : 0.8 },
              }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: STATUS_COLORS[item.status] }} />
              <Typography
                variant="body2"
                sx={{
                  color: cal.textSecondary,
                  fontSize: '0.75rem',
                  textDecoration: isHidden ? 'line-through' : 'none',
                }}
              >
                {item.label} ({countByStatus[item.status] ?? 0})
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Calendar Grid */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${cal.border}`,
          bgcolor: cal.paperBg,
        }}
      >
        {/* Weekday header */}
        <Box sx={{ display: 'flex', bgcolor: cal.headerBg, borderBottom: `1px solid ${cal.border}` }}>
          <Box
            sx={{
              width: 44,
              flexShrink: 0,
              borderRight: `1px solid ${cal.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: cal.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              KW
            </Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {WEEKDAYS.map((day) => (
              <Box
                key={day}
                sx={{
                  py: 1.2,
                  textAlign: 'center',
                  borderRight: `1px solid ${cal.border}`,
                  '&:last-child': { borderRight: 'none' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: cal.accent,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Day cells */}
        {weeks.map((week, weekIdx) => {
          const placements = getWeekEventPlacements(week, filteredEvents, globalLaneMap);
          const maxLane = placements.length > 0 ? Math.max(...placements.map((p) => p.lane)) : -1;
          const barAreaHeight = (maxLane + 1) * 22;

          return (
            <Box
              key={weekIdx}
              sx={{
                display: 'flex',
                borderBottom: weekIdx < weeks.length - 1 ? `1px solid ${cal.border}` : 'none',
              }}
            >
              {/* KW label */}
              <Box
                sx={{
                  width: 44,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  pt: 1,
                  borderRight: `1px solid ${cal.border}`,
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: cal.textMuted, lineHeight: 1 }}>
                  {getISOWeek(week[0])}
                </Typography>
              </Box>

              {/* Grid container */}
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                }}
              >
                {week.map((day, dayIdx) => {
                  const inMonth = isSameMonth(day, currentDate);
                  const today = isToday(day);

                  return (
                    <Box
                      key={dayIdx}
                      sx={{
                        minHeight: 90 + barAreaHeight,
                        minWidth: 0,
                        overflow: 'hidden',
                        borderRight: dayIdx < 6 ? `1px solid ${cal.border}` : 'none',
                        bgcolor: today ? cal.todayBg : !inMonth ? cal.outOfMonthBg : 'transparent',
                        opacity: inMonth ? 1 : 0.4,
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: inMonth ? cal.hoverBg : undefined },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.8, pb: 0 }}>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: today ? 800 : 500,
                            color: today ? cal.accent : inMonth ? cal.textPrimary : cal.textMuted,
                            width: 26,
                            height: 26,
                            lineHeight: '26px',
                            textAlign: 'center',
                            borderRadius: '50%',
                            bgcolor: today ? cal.todayHighlight : 'transparent',
                          }}
                        >
                          {format(day, 'd')}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}

                {/* Vacation bars (absolutely positioned over grid) */}
                {placements.map((placement) => {
                  const { event, startCol, endCol, lane, isStart, isEnd } = placement;
                  const barColor = getStatusColor(event.status);
                  const isHovered = hoveredEventId === event.id;

                  return (
                    <Tooltip key={`bar-${event.id}-w${weekIdx}`} title={renderEventTooltip(event)} arrow>
                      <Box
                        onMouseEnter={() => setHoveredEventId(event.id)}
                        onMouseLeave={() => setHoveredEventId(null)}
                        sx={{
                          position: 'absolute',
                          top: 34 + lane * 22,
                          left: `calc(${(startCol / 7) * 100}% + ${isStart ? 4 : 0}px)`,
                          width: `calc(${((endCol - startCol + 1) / 7) * 100}% - ${(isStart ? 4 : 0) + (isEnd ? 4 : 0)}px)`,
                          height: 20,
                          zIndex: isHovered ? 3 : 2,
                          cursor: 'default',
                          bgcolor: isHovered ? `${barColor}55` : `${barColor}30`,
                          borderLeft: isStart ? `3px solid ${barColor}` : 'none',
                          borderRight: isEnd ? `3px solid ${barColor}` : 'none',
                          borderTop: isHovered ? `1px solid ${barColor}90` : '1px solid transparent',
                          borderBottom: isHovered ? `1px solid ${barColor}90` : '1px solid transparent',
                          borderRadius:
                            isStart && isEnd ? '4px' : isStart ? '4px 0 0 4px' : isEnd ? '0 4px 4px 0' : 0,
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
                          boxShadow: isHovered ? `0 1px 6px ${barColor}40` : 'none',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: barColor,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            px: 0.8,
                          }}
                        >
                          {event.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Paper>

      {filteredEvents.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {events.length === 0
              ? emptyHint ?? `Keine Urlaubsanträge für ${selectedYear} vorhanden`
              : 'Keine Urlaubsanträge für die aktuelle Filterauswahl'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UrlaubKalender;
